import React, { ReactNode } from "react";
import { Button, Modal } from "antd";
import moment from "moment";

import { hasInterestRateChart } from "../../../../helpers/depositType";
import { TChartSlab } from "../../../../helpers/interestRateChart";
import {
  TCreateInvestmentProductState,
  TInvestmentProductDetails,
} from "../CreateInvestmentProduct";
import { needsWithdrawalWindows } from "./planType";
import { WindowUnit } from "./WithdrawalWindowSection";
import { reviewFund, TReviewProblem } from "./reviewFund";
import { Alert, Check } from "./icons";
import styles from "./fundForm.module.scss";

const UNIT_NAME: Record<number, string> = {
  [WindowUnit.Daily]: "days",
  [WindowUnit.Weekly]: "weeks",
  [WindowUnit.Monthly]: "months",
};

const WINDOWS_SHOWN = 3;

interface IReviewFundModal {
  open: boolean;
  state: TCreateInvestmentProductState;
  planTypeLabel?: string;
  submitLabel: string;
  submitting?: boolean;
  footerNote?: string;
  onClose: () => void;
  onGoToSection: (sectionId: string) => void;
  onConfirm: () => void;
}

/**
 * Everything the fund will be created with, before it is.
 *
 * The form is long enough that an oversight is easy and expensive — a fund is
 * read by customers and priced against real money. So Save opens this instead
 * of posting: problems first, each naming where it lives and offering to go
 * there, then the whole fund laid out to be read rather than filled in.
 */
export default function ReviewFundModal({
  open,
  state,
  planTypeLabel,
  submitLabel,
  submitting = false,
  footerNote,
  onClose,
  onGoToSection,
  onConfirm,
}: IReviewFundModal) {
  const problems = reviewFund(state);
  const isReady = problems.length === 0;

  const goTo = (problem: TReviewProblem) => {
    onClose();
    onGoToSection(problem.section);
  };

  const date = (raw?: string) =>
    raw ? moment(raw).format("D MMM YYYY") : undefined;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={940}
      centered
      destroyOnClose
      title={
        <div>
          <div className={styles.reviewHeadline}>Review before submitting</div>
          <div className={styles.reviewSubtitle}>
            Everything this fund will be created with. Check it reads the way a
            customer would expect.
          </div>
        </div>
      }
      footer={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 20,
          }}
        >
          <span className={styles.reviewFooterNote}>{footerNote}</span>
          <div style={{ display: "flex", gap: 12 }}>
            <Button
              size="large"
              onClick={onClose}
              style={{ height: 48, borderRadius: 8 }}
            >
              Back to editing
            </Button>
            <Button
              size="large"
              type="primary"
              disabled={!isReady}
              loading={submitting}
              onClick={onConfirm}
              style={{ height: 48, borderRadius: 8 }}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      }
    >
      {isReady ? (
        <div className={`${styles.reviewBanner} ${styles.reviewBannerOk}`}>
          <Check color="#00a006" />
          Everything needed is filled in
        </div>
      ) : (
        <div className={styles.reviewProblems}>
          <div className={styles.reviewProblemsHead}>
            <Alert color="#ce0000" />
            {problems.length}{" "}
            {problems.length === 1 ? "thing needs" : "things need"} attention
            before this can be submitted
          </div>

          {problems.map((problem) => (
            <button
              key={`${problem.section}-${problem.message}`}
              type="button"
              className={styles.reviewProblem}
              onClick={() => goTo(problem)}
            >
              <span className={styles.stack}>
                <span className={styles.reviewProblemText}>
                  {problem.message}
                </span>
                <span className={styles.reviewProblemWhere}>
                  {problem.where}
                </span>
              </span>
              <span className={styles.reviewProblemGo}>Take me there →</span>
            </button>
          ))}
        </div>
      )}

      <Group title="Investment details">
        <Row label="Investment name" value={state.name} />
        <Row
          label="Plan type"
          value={planTypeLabel}
          optional="Not a savings plan"
        />
        <Row
          label="Subscription window"
          value={
            date(state.startDate) && date(state.endDate)
              ? `${date(state.startDate)} – ${date(state.endDate)}`
              : undefined
          }
        />
        <Row label="About this fund" value={state.description} optional="—" />
        <Row label="Objective" value={state.objective} optional="—" />
        <Row
          label="Issuer · target"
          value={
            state.issuers || state.targetInvestors
              ? [state.issuers, state.targetInvestors]
                  .filter(Boolean)
                  .join(" · ")
              : undefined
          }
          optional="—"
        />
      </Group>

      <Group title="Visibility & documents">
        <Row
          label="Visible to customers"
          value={
            state.isCustomerFacing
              ? "Yes · customers can subscribe on Digital Bank"
              : "No · hidden from customers"
          }
        />
        <Row
          label="Term sheet"
          value={state.termSheetFile?.fileName}
          optional="None attached"
        />
      </Group>

      {needsWithdrawalWindows(state.productTypeId) ? (
        <Group title="Withdrawal windows" badge="Habit Save">
          <Row
            label="A window opens every"
            value={
              state.withdrawalWindowInterval
                ? `${state.withdrawalWindowInterval} ${
                    UNIT_NAME[
                      state.withdrawalWindowIntervalUnit ?? WindowUnit.Monthly
                    ]
                  }`
                : undefined
            }
          />
          <Row
            label="And stays open for"
            value={
              state.gracePeriod === null || state.gracePeriod === undefined
                ? undefined
                : `${state.gracePeriod} days`
            }
          />
          <Row
            label={`First ${WINDOWS_SHOWN} windows`}
            value={describeWindows(state)}
            muted
            optional="—"
          />
        </Group>
      ) : null}

      {state.investmentProductDetails.map((detail, index) => (
        <Group
          key={detail?.id ?? detail?.value ?? index}
          title={`Financials — ${detail?.label || detail?.currencyCode}`}
          badge={`${index + 1} of ${state.investmentProductDetails.length} ${
            state.investmentProductDetails.length === 1
              ? "currency"
              : "currencies"
          }`}
        >
          <CurrencyRows detail={detail} />
        </Group>
      ))}
    </Modal>
  );
}

function CurrencyRows({ detail }: { detail: TInvestmentProductDetails }) {
  const currency = detail?.currencyCode || detail?.value || "";
  const money = (value?: number) =>
    value === undefined || value === null || Number.isNaN(Number(value))
      ? undefined
      : `${currency === "NGN" ? "₦" : `${currency} `}${Number(
          value,
        ).toLocaleString()}`;

  const limits =
    money(detail?.minimumInvestment) && money(detail?.maximumInvestment)
      ? `${money(detail?.minimumInvestment)} – ${money(
          detail?.maximumInvestment,
        )}`
      : undefined;

  const chart = detail?.interestRateChart;

  return (
    <>
      <Row
        label="Core banking"
        value={
          detail?.coreBankingTenantId && detail?.coreBankingProductId
            ? `${detail.coreBankingTenantId} · product ${detail.coreBankingProductId}`
            : undefined
        }
      />
      <Row label="Investment limits" value={limits} />
      <Row
        label="Early liquidation"
        value={
          detail?.monthlyPrematureLiquidationRate !== undefined
            ? `${detail.monthlyPrematureLiquidationRate}% monthly · ${detail.endOfTenurePrematureLiquidationRate}% end of tenor`
            : undefined
        }
        optional="—"
      />

      {hasInterestRateChart(detail?.depositType) ? (
        chart?.chartSlabs?.length ? (
          <div className={styles.reviewLadder}>
            <div className={styles.reviewLadderHead}>
              <div>From</div>
              <div>Up to</div>
              <div>Rate</div>
              <div>Label</div>
            </div>
            {chart.chartSlabs.map((slab: TChartSlab, index: number) => {
              const isLast = index === chart.chartSlabs.length - 1;
              const amount = (value?: number | null) =>
                value === null || value === undefined
                  ? ""
                  : `${currency === "NGN" ? "₦" : ""}${Number(
                      value,
                    ).toLocaleString()}`;

              return (
                <div key={index} className={styles.reviewLadderRow}>
                  <div style={{ fontWeight: 500 }}>
                    {amount(slab.fromAmount)}
                  </div>
                  <div className={isLast ? styles.reviewRowMuted : undefined}>
                    {isLast ? "and above" : amount(slab.toAmount)}
                  </div>
                  <div style={{ fontWeight: 500, color: "#3F137D" }}>
                    {slab.interestRate}%
                  </div>
                  <div className={styles.reviewRowMuted}>
                    {slab.description}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Row label="Rate chart" value={undefined} />
        )
      ) : null}
    </>
  );
}

function describeWindows(state: TCreateInvestmentProductState) {
  const interval = state.withdrawalWindowInterval;

  if (!interval) {
    return undefined;
  }

  const unit = state.withdrawalWindowIntervalUnit ?? WindowUnit.Monthly;
  const step: Record<number, moment.unitOfTime.DurationConstructor> = {
    [WindowUnit.Daily]: "days",
    [WindowUnit.Weekly]: "weeks",
    [WindowUnit.Monthly]: "months",
  };

  return Array.from({ length: WINDOWS_SHOWN }, (_unused, i) =>
    moment()
      .add(interval * (i + 1), step[unit])
      .format("D MMM YYYY"),
  ).join(" · ");
}

function Group({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.reviewGroup}>
      <div className={styles.reviewGroupHead}>
        <div className={styles.reviewGroupTitle}>{title}</div>
        {badge ? <span className={styles.tabChip}>{badge}</span> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * One line of the summary.
 *
 * `optional` is what to show when the value is empty and that is allowed. With
 * no `optional`, an empty value is called out — the field is one the fund
 * cannot be created without.
 */
function Row({
  label,
  value,
  optional,
  muted,
}: {
  label: string;
  value?: string;
  optional?: string;
  muted?: boolean;
}) {
  const isEmpty = value === undefined || value === null || value === "";

  return (
    <div className={styles.reviewRow}>
      <div className={styles.reviewRowLabel}>{label}</div>
      <div
        className={`${styles.reviewRowValue} ${muted ? styles.reviewRowMuted : ""}`}
      >
        {!isEmpty ? (
          value
        ) : optional ? (
          <span className={styles.reviewRowMuted}>{optional}</span>
        ) : (
          <span className={styles.reviewMissing}>
            <Alert size={14} color="#ce0000" />
            Not set
          </span>
        )}
      </div>
    </div>
  );
}
